import { DateTime, Interval } from 'luxon';
import * as moment from 'moment';

import { IBusySlot } from './availability.interface';
import { IRecurrencyAttempt } from './recurrent-attempts.interface';

export interface TypeValue {
	type: string;
	value: number;
}

export interface SubscriptionProfessional {
	compensation?: TypeValue;
	discount?: TypeValue;
}

export interface CompensationInfo {
	subscription?: {
		id: string;
		type: string;
		professional?: SubscriptionProfessional | null;
		supportedByProfessional?: boolean | null;
	};
	contact?: {
		id: number;
		sessionPrice?: number | null;
		invitedByProfessional?: boolean | null;
	};
	voucher?: {
		id: string;
		supportedByZenklub: boolean | null;
		professionalDiscount?: TypeValue;
		value: number;
		type: string;
	};
}

export interface CompensationInfoText {
	subscription?: {
		professional?: {
			compensation?: string;
			discount?: string;
		};
		supportedByProfessional?: string;
	};
	contact?: {
		sessionPrice?: string;
		invitedByProfessional?: string;
	};
	voucher?: {
		professionalDiscount?: string;
	};
}

export interface ICalendarEvent extends IEvent {
	recurringInviteScheduleId?: string;
	b2cPaidPrice?: number;
	calendarHeight?: string;
	rescheduledFrom?: string;
	calendarOffset?: string;
	calendarTime?: string;
	canBeRescheduled?: boolean;
	cattimestamp: number;
	duration: number;
	endTime?: string;
	hasPromptuary?: boolean;
	isB2C?: boolean;
	noShow?: boolean;
	startTime?: string;
	statusText?: string;
	timestamp: number;
	uattimestamp: number;
	origin?: string;
	userImageUrl?: string;
	triageId?: string;
	timeZone?: {
		name: string;
		minutesToUTC: number;
	};
	userRequestedReschedule?: boolean;
	professionalAddress?: IProfessionalAddress;
	inviteExpirationDate?: string;
}
// Interface for current day events.
export interface IEvent {
	id: string;
	professionalName: string;
	userName: string;
	chargeId: string;
	price: number;
	originalPrice: number;
	discount: number;
	professionalCompensation?: number;
	voucherDescription?: string;
	duration: number;
	contactReason: string[];
	isOnlinePayment?: boolean;
	cancellation?: {
		cancelledDate: Date;
		cancelledBy: string;
		previousStatus: Date;
	};
	reschedule?: {
		eventId: string;
		rescheduledBy: string;
		rescheduleUtcStartDate: Date;
		rescheduleUtcEndDate: Date;
	};
	subscription?: {
		subscriptionId: string;
		planId?: string;
		description: string;
		sessionModel?: string;
		coordinatedCareType?: string;
	};
	country: string;
	currency: string;
	status: string;
	createdAt: Date;
	updatedAt: Date;
	receiptSentAt?: Date;
	receiptUrl?: string;
	cantEmitReceipt?: boolean;
	confirmedAt?: Date;
	professionalId: string;
	profileId: string;
	contactId?: number | undefined;
	day?: Date;
	dayEnd?: Date;
	compensationInfo?: CompensationInfo;
	compensationInfoText?: CompensationInfoText;
	isHalfHourSession?: boolean;
	jitsiDomain?: string;
	compensationFinal?: {
		compensation?: number | null;
		compensationInfo?: CompensationInfo | null;
		compensationInfoText?: CompensationInfoText | null;
		calculatedAt?: Date | null;
	};
	observation?: string;
	errorCode?: number;
	protocol?: string;
}

export interface IProfessionalAddress {
	id?: string;
	name?: string;
	postalCode: string;
	street: string;
	number: string;
	neighborhood: string;
	city: string;
	state: string;
	country: string;
	complement?: string;
	phone: string;
	professionalId: string;
	latitude?: number;
	longitude?: number;
	plusCode?: string;
	removedAt?: Date | null;
}

export interface IEventList {
	total: number;
	items: IEvent[];
}

// Interface for new appointments.
export interface IRecurrencyAppointment {
	inviteExpiresMinutesBeforeSessions?: number;
	userId?: string;
	contactId?: number;
	price: number;
	sessions: string[];
	contactReason?: string[];
	professionalAddressId?: string;
	recurrencyType?: string;
	totalSessions?: number;
}

export interface INewEvent {
	id?: string;
	country?: string;
	type: string;
	professionalOnline: boolean;
	professionalId: string;
	userId?: string;
	contactId?: number;
	contactName?: string;
	price: number;
	inviteExpiresMinutesBeforeSessions?: number;
	startDate: string;
	timezone: string;
	contactReason?: string[];
	tags?: number[];
	professionalAddressId?: string;
	repositionOriginalEventId?: string;
}

// Interface for update event.
export interface IUpdateEvent {
	userId?: string;
	timeZone?: string;
	startDate?: string;
	contactReason?: string[];
	professionalAddressId?: string;
}

// Interface for confirm event.
export interface IConfirmEvent {
	contactReason?: string[];
}

export interface IValidateStartResponse {
	result: boolean;
	jitsiDomain?: string;
}

// Interface for daily events.
export interface IDayEvents {
	luxonDate: DateTime;
	hour: string;
	events: ICalendarEvent[];
	busySlots: IBusySlot[];
	isHalfHour: boolean;
	recurrencyAttempts: IRecurrencyAttempt[];
	hasRecurrencyOverlaping?: boolean;
	interval: Interval;
}

// Interface for each day events.
export interface IWeekDayEvents {
	luxonDate: DateTime;
	weekDay: number;
	events: ICalendarEvent[];
	busySlots: IBusySlot[];
	recurrencyAttempts: IRecurrencyAttempt[];
	hasRecurrencyOverlaping?: boolean;
	interval: Interval;
}

// Interface for daily events.
export interface IWeekEvents {
	hour: string;
	weekEvents: IWeekDayEvents[];
}

export interface SearchEvents {
	userIds?: string[];
	status?: string[];
	skip?: number;
	limit?: number;
	minDate?: moment.Moment;
	maxDate?: moment.Moment;
	lastUpdate?: boolean;
	setWeek?: boolean;
	ignoreDates?: boolean;
	onlyMissingTags?: boolean;
}

export interface EventPrice {
	title: string;
	extraInfo?: string[];
	value?: string;
	valueNumber?: number;
}

export function isIEvent(object: any): boolean {
	return object.status !== undefined;
}
